import React, { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  Package,
  Activity,
  Zap,
  Bus,
  TrendingUp,
  AlertTriangle,
  CheckCircle2,
  CircleDot,
  Route,
  CalendarCheck,
} from "lucide-react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
  CartesianGrid,
} from "recharts";
import { useData } from "../lib/data-context";
import { fmtInt, fmtDec, fmtMWh, viabilidadColor } from "../lib/api";
import KPICard from "../components/KPICard";
import Semaforo from "../components/Semaforo";
import ConsumoTipoDia from "../components/ConsumoTipoDia";

const VIAB_COLORS = ["#15803D", "#B45309", "#991B1B"];
const PALETTE = ["#14532D", "#0F1A14", "#6B7280", "#991B1B", "#15803D", "#B45309", "#0891B2", "#7C2D12"];

export default function Dashboard() {
  const nav = useNavigate();
  const { lineas, loading } = useData();

  const data = useMemo(() => {
    if (!lineas?.length) return null;
    const total = lineas.length;
    const lotes = new Set(lineas.map((l) => l.lote));
    const consAnual = lineas.reduce((s, l) => s + l.consumo_anual_estimado_kwh, 0);
    const consMedio = lineas.reduce((s, l) => s + l.consumo_kwh_km_base, 0) / total;
    const vehKmTotal = lineas.reduce((s, l) => s + (l.veh_km_anuales || 0), 0);
    const expedTotal = lineas.reduce((s, l) => s + (l.expediciones_anuales || 0), 0);
    const vehiculos = lineas.reduce(
      (s, l) => s + Math.max(1, Math.floor(l.veh_km_anuales / 60000)),
      0
    );
    const viab = { Alta: 0, Condicionada: 0, "A revisar": 0 };
    const porLote = {};
    const porTip = {};
    const porEt = {};
    lineas.forEach((l) => {
      viab[l.viabilidad] = (viab[l.viabilidad] || 0) + 1;
      porLote[l.lote] = (porLote[l.lote] || 0) + 1;
      const claseSize = `${l.clase_vehiculo} ${l.tamano_vehiculo}`;
      porTip[claseSize] = (porTip[claseSize] || 0) + 1;
      porEt[l.etiqueta_ambiental] = (porEt[l.etiqueta_ambiental] || 0) + 1;
    });
    const top10 = [...lineas]
      .sort((a, b) => b.consumo_kwh_km_base - a.consumo_kwh_km_base)
      .slice(0, 10);
    const topAnual = [...lineas]
      .sort((a, b) => b.consumo_anual_estimado_kwh - a.consumo_anual_estimado_kwh)
      .slice(0, 10);
    const topDesnivel = [...lineas]
      .filter((l) => l.ascenso_por_km != null && l.ascenso_por_km > 0)
      .sort((a, b) => (b.ascenso_por_km || 0) - (a.ascenso_por_km || 0))
      .slice(0, 10);
    return {
      total,
      lotes: lotes.size,
      consAnual,
      consMedio,
      vehiculos,
      vehKmTotal,
      expedTotal,
      viab,
      porLote: Object.entries(porLote).map(([lote, n]) => ({ lote, n })),
      porTip: Object.entries(porTip)
        .map(([t, n]) => ({ tipologia: t, n }))
        .sort((a, b) => b.n - a.n),
      porEt: Object.entries(porEt).map(([etiqueta, n]) => ({ etiqueta, n })),
      top10,
      topAnual,
      topDesnivel,
    };
  }, [lineas]);

  if (loading || !data) return <Loading />;

  const viabData = Object.entries(data.viab).map(([name, value]) => ({
    name,
    value,
  }));

  return (
    <div className="space-y-7" data-testid="dashboard-page">
      <div>
        <div className="text-xs font-semibold tracking-widest text-gray-500 uppercase">
          Dashboard ejecutivo
        </div>
        <h1 className="section-title mt-1">Estudio de viabilidad eléctrica</h1>
        <p className="section-sub">
          Visión agregada de las concesiones cargadas. Factor de
          consumo kWh/km asignado por línea según Anexo VIII y resultados del
          estudio.
        </p>
      </div>

      {/* KPI row */}
      <div className="grid grid-cols-2 lg:grid-cols-4 xl:grid-cols-7 gap-4">
        <KPICard
          testId="kpi-lotes"
          label="Lotes analizados"
          value={data.lotes}
          hint="Concesiones cargadas"
          icon={Package}
          tone="blue"
        />
        <KPICard
          testId="kpi-lineas"
          label="Total líneas"
          value={fmtInt(data.total)}
          hint="Líneas en estudio"
          icon={Activity}
          tone="blue"
        />
        <KPICard
          testId="kpi-vehkm"
          label="Veh-km/año"
          value={fmtInt(data.vehKmTotal)}
          hint="Anexo V (real)"
          icon={Route}
          tone="green"
        />
        <KPICard
          testId="kpi-expediciones"
          label="Expediciones/año"
          value={fmtInt(data.expedTotal)}
          hint="Suma del Anexo V"
          icon={CalendarCheck}
          tone="green"
        />
        <KPICard
          testId="kpi-vehiculos"
          label="Vehículos estimados"
          value={fmtInt(data.vehiculos)}
          hint="Estimación operativa"
          icon={Bus}
        />
        <KPICard
          testId="kpi-consumo-medio"
          label="Consumo medio"
          value={`${fmtDec(data.consMedio, 2)} kWh/km`}
          hint="Factor base ponderado"
          icon={Zap}
          tone="green"
        />
        <KPICard
          testId="kpi-consumo-anual"
          label="Consumo anual total"
          value={fmtMWh(data.consAnual)}
          hint={`${fmtInt(data.consAnual)} kWh`}
          icon={TrendingUp}
          tone="red"
        />
      </div>

      {/* Viability summary */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
        {[
          {
            key: "Alta",
            count: data.viab.Alta,
            icon: CheckCircle2,
            color: "#15803D",
            bg: "bg-green-50",
            label: "Viabilidad alta",
          },
          {
            key: "Condicionada",
            count: data.viab.Condicionada,
            icon: CircleDot,
            color: "#B45309",
            bg: "bg-amber-50",
            label: "Viabilidad condicionada",
          },
          {
            key: "A revisar",
            count: data.viab["A revisar"],
            icon: AlertTriangle,
            color: "#991B1B",
            bg: "bg-red-50",
            label: "A revisar",
          },
        ].map((v) => {
          const Icon = v.icon;
          const pct = ((v.count / data.total) * 100).toFixed(1);
          return (
            <div key={v.key} className="crtm-card p-5">
              <div className="flex items-center gap-4">
                <div
                  className={`w-12 h-12 rounded-lg ${v.bg} flex items-center justify-center`}
                >
                  <Icon size={22} strokeWidth={1.75} style={{ color: v.color }} />
                </div>
                <div className="flex-1">
                  <div className="kpi-label">{v.label}</div>
                  <div className="kpi-value">{v.count}</div>
                  <div className="text-xs text-gray-500 mt-0.5">
                    {pct}% del total
                  </div>
                </div>
              </div>
              <div className="mt-4 h-2 w-full bg-gray-100 rounded-full overflow-hidden">
                <div
                  className="h-full rounded-full"
                  style={{ width: `${pct}%`, background: v.color }}
                />
              </div>
            </div>
          );
        })}
      </div>

      {/* Charts row 1 */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        <ChartCard title="Distribución de líneas por lote" subtitle="Número de líneas en cada lote">
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={data.porLote} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
              <CartesianGrid stroke="#F3F4F6" vertical={false} />
              <XAxis dataKey="lote" tick={{ fontSize: 12, fill: "#4B5563" }} />
              <YAxis tick={{ fontSize: 11, fill: "#9CA3AF" }} />
              <Tooltip contentStyle={tooltipStyle} />
              <Bar dataKey="n" fill="#14532D" radius={[4, 4, 0, 0]} maxBarSize={48} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Viabilidad eléctrica" subtitle="Distribución por nivel">
          <ResponsiveContainer width="100%" height={260}>
            <PieChart>
              <Pie
                data={viabData}
                dataKey="value"
                nameKey="name"
                cx="50%"
                cy="50%"
                innerRadius={55}
                outerRadius={88}
                paddingAngle={2}
              >
                {viabData.map((_, i) => (
                  <Cell key={i} fill={VIAB_COLORS[i]} />
                ))}
              </Pie>
              <Tooltip contentStyle={tooltipStyle} />
              <Legend
                iconType="circle"
                wrapperStyle={{ fontSize: 12, color: "#4B5563" }}
              />
            </PieChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Etiqueta ambiental" subtitle="Líneas por etiqueta">
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={data.porEt} layout="vertical" margin={{ left: 10 }}>
              <CartesianGrid stroke="#F3F4F6" horizontal={false} />
              <XAxis type="number" tick={{ fontSize: 11, fill: "#9CA3AF" }} />
              <YAxis dataKey="etiqueta" type="category" tick={{ fontSize: 12, fill: "#4B5563" }} width={90} />
              <Tooltip contentStyle={tooltipStyle} />
              <Bar dataKey="n" fill="#0F1A14" radius={[0, 4, 4, 0]} maxBarSize={20} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>

      {/* Consumo por tipo de día (toda la flota) */}
      <ConsumoTipoDia title="Consumo por tipo de día — Toda la flota" />

      {/* Tipología distribution */}
      <ChartCard
        title="Líneas por tipología de vehículo"
        subtitle="Clasificación Anexo VIII (clase y tamaño)"
      >
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={data.porTip} margin={{ top: 10, right: 20, bottom: 40, left: 10 }}>
            <CartesianGrid stroke="#F3F4F6" vertical={false} />
            <XAxis
              dataKey="tipologia"
              tick={{ fontSize: 11, fill: "#4B5563" }}
              angle={-25}
              textAnchor="end"
              interval={0}
              height={70}
            />
            <YAxis tick={{ fontSize: 11, fill: "#9CA3AF" }} />
            <Tooltip contentStyle={tooltipStyle} />
            <Bar dataKey="n" fill="#14532D" radius={[4, 4, 0, 0]} maxBarSize={50} />
          </BarChart>
        </ResponsiveContainer>
      </ChartCard>

      {/* Rankings */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        <RankingTable
          title="Ranking · Mayor factor kWh/km"
          rows={data.top10}
          metric={(l) => `${fmtDec(l.consumo_kwh_km_base, 2)} kWh/km`}
          onClick={(l) => nav(`/linea/${encodeURIComponent(l.codigo_linea)}`)}
          testid="ranking-kwhkm"
        />
        <RankingTable
          title="Ranking · Mayor consumo anual"
          rows={data.topAnual}
          metric={(l) => fmtMWh(l.consumo_anual_estimado_kwh)}
          onClick={(l) => nav(`/linea/${encodeURIComponent(l.codigo_linea)}`)}
          testid="ranking-anual"
        />
        {data.topDesnivel?.length > 0 && (
          <RankingTable
            title="Ranking · Mayor desnivel m/km"
            rows={data.topDesnivel}
            metric={(l) => `${fmtDec(l.ascenso_por_km || 0, 1)} m/km`}
            onClick={(l) => nav(`/linea/${encodeURIComponent(l.codigo_linea)}`)}
            testid="ranking-desnivel"
          />
        )}
      </div>
    </div>
  );
}

const tooltipStyle = {
  background: "white",
  border: "1px solid #E5E7EB",
  borderRadius: 8,
  fontSize: 12,
  boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
};

const ChartCard = ({ title, subtitle, children }) => (
  <div className="crtm-card p-5">
    <div className="mb-3">
      <h3 className="font-heading font-semibold text-gray-900 text-[15px]">{title}</h3>
      {subtitle && (
        <p className="text-xs text-gray-500 mt-0.5">{subtitle}</p>
      )}
    </div>
    {children}
  </div>
);

const RankingTable = ({ title, rows, metric, onClick, testid }) => (
  <div className="crtm-card" data-testid={testid}>
    <div className="p-5 border-b border-gray-100">
      <h3 className="font-heading font-semibold text-gray-900 text-[15px]">{title}</h3>
    </div>
    <table className="crtm-table">
      <thead>
        <tr>
          <th style={{ width: 40 }}>#</th>
          <th>Línea</th>
          <th>Lote</th>
          <th>Viabilidad</th>
          <th className="text-right" style={{ textAlign: "right" }}>
            Métrica
          </th>
        </tr>
      </thead>
      <tbody>
        {rows.map((l, i) => (
          <tr key={l.codigo_linea} className="clickable" onClick={() => onClick(l)}>
            <td className="text-gray-400 font-mono">{i + 1}</td>
            <td>
              <div className="font-semibold">{l.numero_linea}</div>
              <div className="text-xs text-gray-500 truncate max-w-[260px]">
                {l.denominacion}
              </div>
            </td>
            <td>
              <span className="pill neutral">{l.lote}</span>
            </td>
            <td>
              <Semaforo viabilidad={l.viabilidad} />
            </td>
            <td className="font-mono text-right">{metric(l)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const Loading = () => (
  <div className="flex items-center justify-center h-full min-h-[400px]">
    <div className="text-gray-500 text-sm">Cargando datos…</div>
  </div>
);
